import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import ListingCard from "@/components/ListingCard";
import { publicApi, errMsg } from "@/lib/api";
import { toast } from "sonner";

const STEPS = [
  { key: "feeling", q: "What do you need most right now?", opts: [["quiet-corners", "Quiet", "Silence, rest, a view to sit with"], ["creative-resets", "To make something", "Clay, words, hands busy"], ["movement", "To move", "Trails, yoga, a tired good body"], ["nature-escapes", "To be outside", "Lakes, dunes, stars"]] },
  { key: "time", q: "How much time can you take?", opts: [["full-day", "One day", "Back home by night"], ["weekend", "A weekend", "Two or three days"], ["multi-day", "Longer", "Four days or more"]] },
  { key: "budget", q: "What's your budget per person?", opts: [["affordable", "Up to PKR 40k", "Affordable resets"], ["mid", "PKR 40k–120k", "Most mountain trips"], ["premium", "PKR 120k+", "Long and outbound"], ["any", "Flexible", "Show me the best fit"]] },
  { key: "setting", q: "Where do you picture yourself?", opts: [["mountains", "Mountains", "Hunza, Skardu"], ["forest", "Pine forest", "Galiyat, Murree"], ["water", "By water", "Lakes and rivers"], ["desert", "Desert", "Thar and beyond"], ["city", "In the city", "Lahore days"], ["abroad", "Abroad", "Outbound corridors"]] },
  { key: "company", q: "Who are you going with?", opts: [["solo", "Just me", "Join a small cohort"], ["friends", "A friend or two", "Book a few seats"], ["women-only", "Women-only group", "Women-led cohorts"], ["private", "My own group", "Buy out the whole getaway"]] },
];

export default function GuideMe() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);

  const pick = async (val) => {
    const next = { ...answers, [STEPS[step].key]: val };
    setAnswers(next);
    if (step < STEPS.length - 1) return setStep(step + 1);
    try {
      const { data } = await publicApi.post("/discover/match", next);
      setResults(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reset = () => { setStep(0); setAnswers({}); setResults(null); };

  if (results) {
    return (
      <div className="mx-auto max-w-7xl px-5 py-14 md:px-8" data-testid="guide-results">
        <div className="eyebrow text-secondary">Your matches</div>
        <h1 className="mt-2 text-4xl tracking-tight text-primary sm:text-5xl">Here's where we'd send you.</h1>
        <Button variant="ghost" className="mt-4 rounded-full" onClick={reset} data-testid="guide-restart-btn"><RotateCcw className="mr-2 h-4 w-4" />Start over</Button>
        {results.length === 0 ? (
          <p className="mt-10 text-muted-foreground" data-testid="guide-no-results">No close matches right now. <Link className="text-secondary underline" to="/explore">Browse everything</Link>.</p>
        ) : (
          <div className="mt-10 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((l) => <ListingCard key={l.id} l={l} reasons={l.match_reasons} />)}
          </div>
        )}
      </div>
    );
  }

  const s = STEPS[step];
  return (
    <div className="mx-auto max-w-3xl px-5 py-14 md:px-8" data-testid="guide-me-page">
      <div className="flex items-center justify-between">
        <div className="eyebrow text-secondary">Guide me · {step + 1} of {STEPS.length}</div>
        {step > 0 && <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)} data-testid="guide-back-btn"><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>}
      </div>
      <Progress value={((step + 1) / STEPS.length) * 100} className="mt-4 h-1" />
      <h1 key={s.key} className="rise mt-10 text-3xl tracking-tight text-primary sm:text-4xl">{s.q}</h1>
      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {s.opts.map(([v, title, sub]) => (
          <button key={v} onClick={() => pick(v)} className="rise group rounded-xl border border-border bg-white p-5 text-left transition-colors duration-200 hover:border-secondary hover:bg-secondary/5" data-testid={`guide-option-${s.key}-${v}`}>
            <div className="font-display text-xl text-foreground group-hover:text-secondary">{title}</div>
            <div className="mt-1 text-sm text-muted-foreground">{sub}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
